// src/components/Roadmap.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../utilities/AuthContext';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;
const STATUS_ORDER = ['open', 'todo', 'doing', 'done'];

export default function Roadmap() {
  const [auth] = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isAdmin = auth && auth.role && auth.role.includes('admin');

  useEffect(() => {
    fetchRoadmap();
  }, []);

  const fetchRoadmap = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/feedback/roadmap`, {
        withCredentials: true,
      });
      setItems(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch roadmap');
    } finally {
      setLoading(false);
    }
  };

  const getNextStatus = (current) => {
    const cur = (current || 'open').toLowerCase();
    const idx = STATUS_ORDER.indexOf(cur);
    if (idx === -1) return 'open';
    return STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
  };

  const handleStatusChange = async (item) => {
    if (!isAdmin) return;

    const next = getNextStatus(item.status);

    try {
      const res = await axios.post(
        `${API_BASE}/feedback/update_status`,
        { feedbackId: item._id, status: next },
        { withCredentials: true }
      );

      const updated = res.data;
      setItems((prev) =>
        prev.map((it) => (it._id === updated._id ? updated : it))
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update status');
    }
  };

  const getColumnTheme = (statusKey) => {
    switch (statusKey) {
      case 'open':
        return {
          header: 'from-slate-800 to-slate-700',
          pill: 'bg-slate-100 text-slate-700 border border-slate-200',
          dot: 'bg-slate-500',
        };
      case 'todo':
        return {
          header: 'from-sky-800 to-cyan-700',
          pill: 'bg-sky-100 text-sky-700 border border-sky-200',
          dot: 'bg-sky-500',
        };
      case 'doing':
        return {
          header: 'from-amber-600 to-orange-500',
          pill: 'bg-amber-100 text-amber-700 border border-amber-200',
          dot: 'bg-amber-500',
        };
      case 'done':
        return {
          header: 'from-emerald-700 to-teal-600',
          pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
          dot: 'bg-emerald-500',
        };
      default:
        return {
          header: 'from-slate-800 to-slate-700',
          pill: 'bg-slate-100 text-slate-700 border border-slate-200',
          dot: 'bg-slate-500',
        };
    }
  };

  const renderColumn = (label, statusKey) => {
    const filtered = items.filter(
      (it) => (it.status || 'open').toLowerCase() === statusKey
    );

    const theme = getColumnTheme(statusKey);

    return (
      <div className="rounded-[28px] border border-white/10 bg-white/90 shadow-2xl backdrop-blur overflow-hidden">
        <div className={`bg-gradient-to-r ${theme.header} px-4 py-4`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${theme.dot}`} />
              <h3 className="text-lg font-bold text-white">{label}</h3>
            </div>
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white">
              {filtered.length}
            </span>
          </div>
        </div>

        <div className="min-h-[500px] bg-slate-50 p-4">
          <div className="space-y-4">
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-400">
                No items
              </div>
            ) : (
              filtered.map((item) => (
                <div
                  key={item._id}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${theme.pill}`}>
                      {statusKey.toUpperCase()}
                    </span>

                    <span className="inline-flex rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                      {item.vote ? item.vote.length : 0} vote
                      {item.vote && item.vote.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-slate-900">
                      {item.title || 'Untitled'}
                    </h4>
                    <p
                      className="mt-2 text-sm leading-6 text-slate-600 line-clamp-4 hover:line-clamp-none transition-all duration-300 overflow-hidden"
                    >
                      {item.note}
                    </p>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-400">
                      {item.firstName || item.lastName
                        ? `${item.firstName || ''} ${item.lastName || ''}`
                        : 'Community feedback'}
                    </div>

                    {isAdmin ? (
                      <button
                        onClick={() => handleStatusChange(item)}
                        className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                      >
                        Move to {getNextStatus(item.status).toUpperCase()}
                      </button>
                    ) : (
                      <span className="rounded-xl bg-slate-200 px-3 py-2 text-xs font-semibold text-slate-600">
                        {(item.status || 'open').toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const openCount = items.filter(
    (x) => (x.status || 'open').toLowerCase() === 'open'
  ).length;
  const todoCount = items.filter(
    (x) => (x.status || 'open').toLowerCase() === 'todo'
  ).length;
  const doingCount = items.filter(
    (x) => (x.status || 'open').toLowerCase() === 'doing'
  ).length;
  const doneCount = items.filter(
    (x) => (x.status || 'open').toLowerCase() === 'done'
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-sky-950 to-cyan-950 px-4 py-8 md:px-8 lg:px-12">
      <div className="mx-auto max-w-8xl">
        {/* hero */}
        <div className="mb-8 overflow-hidden rounded-[28px] border border-white/10 bg-white/8 backdrop-blur-xl shadow-2xl">
          <div className="grid gap-6 p-6 md:grid-cols-[1.2fr_0.8fr] md:p-8">
            <div>
              <div className="mb-3 inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
                Product progress
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-white md:text-5xl">
                Roadmap
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 md:text-base">
                Track feature requests, bugs, improvements, and what the team is
                planning, building, and shipping.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Open
                </p>
                <p className="mt-2 text-2xl font-bold text-white">{openCount}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Todo
                </p>
                <p className="mt-2 text-2xl font-bold text-white">{todoCount}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Doing
                </p>
                <p className="mt-2 text-2xl font-bold text-white">{doingCount}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Done
                </p>
                <p className="mt-2 text-2xl font-bold text-white">{doneCount}</p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-red-300/30 bg-red-500/10 px-4 py-3 text-sm text-red-100 backdrop-blur">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-3xl border border-white/10 bg-white/10 px-6 py-10 text-center text-slate-200 backdrop-blur">
            Loading roadmap…
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-5 xl:grid-cols-4">
            {renderColumn('Open', 'open')}
            {renderColumn('Todo', 'todo')}
            {renderColumn('Doing', 'doing')}
            {renderColumn('Done', 'done')}
          </div>
        )}
      </div>
    </div>
  );
}